import * as reactIntl from 'react-intl';

import {
  isHemeraActiveClient,
  isStinvestActiveClient,
} from '»utils/clientsUtils';
import { yup } from '»utils/form';

export const naturalPersonFormValidation = (mainFormFields) => {
  const intl = reactIntl.useIntl();

  const requiredFieldMessage = intl.formatMessage({
    id: 'TRLT_UI-REQUIRED_FIELD',
  });

  const fieldMustBeNumber = intl.formatMessage({
    id: 'TRLT_UI-NUMBER_FIELD',
  });

  const fieldMustBeEmail = intl.formatMessage({
    id: 'TRLT_UI-INVALID_EMAIL',
  });

  const fieldCanNotHaveSpaces = intl.formatMessage({
    id: 'TRLT_UI-NO_SPACES_ALLOW',
  });

  const fieldIntegerOnly = intl.formatMessage({
    id: 'TRLT_UI-INTEGER_FIELD',
  });

  const ibanFormatValidation = intl.formatMessage({
    id: 'TRLT_UI-IBAN_FORMAT_VALIDATION',
  });

  const angolanIbanFormatValidation = intl.formatMessage({
    id: 'TRLT_UI-ANGOLAN_IBAN_FORMAT_VALIDATION',
  });

  const ibanMustBeDifferent = intl.formatMessage({
    id: 'TRLT_UI-IBAN_MUST_BE_DIFFERENT',
  });

  const amountGreaterOrEqual = (minValue) =>
    intl.formatMessage(
      {
        id: 'TRLT_UI-AMOUNT_BIGGER',
      },
      { min: minValue },
    );

  const getFieldObligation = (fieldName) =>
    mainFormFields.find((field) => field.name === fieldName)?.isRequired;

  return yup.object().shape({
    name: yup.string().when([], {
      is: () => getFieldObligation('name'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    idType: yup.string().when([], {
      is: () => getFieldObligation('idType'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    otherIdType: yup.string().when(['idType'], {
      is: (idType) => idType === 'OUTRO' && getFieldObligation('otherIdType'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    idNumber: yup.string().when([], {
      is: () => getFieldObligation('idNumber'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    nif: yup.string().when([], {
      is: () => getFieldObligation('nif'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    idEntity: yup.string().when([], {
      is: () => getFieldObligation('idEntity'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    idExpDate: yup.date().when([], {
      is: () => getFieldObligation('idExpDate'),
      then: () => yup.date().required(requiredFieldMessage),
      otherwise: () => yup.date(),
    }),
    idIssueLocal: yup.string().when([], {
      is: () => getFieldObligation('idIssueLocal'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    idIssueDate: yup.date().when([], {
      is: () => getFieldObligation('idIssueDate'),
      then: () => yup.date().required(requiredFieldMessage),
      otherwise: () => yup.date(),
    }),
    placeOfBirth: yup.string().when([], {
      is: () => getFieldObligation('placeOfBirth'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    nationality: yup.string().when([], {
      is: () => getFieldObligation('nationality'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    otherNationalities: yup.string().when([], {
      is: () => getFieldObligation('otherNationalities'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    educQualifications: yup.string().when([], {
      is: () => getFieldObligation('educQualifications'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    iban: yup.string().when([], {
      is: () => getFieldObligation('iban'),
      then: () =>
        yup
          .string()
          .ibanAo06FormatValidation(angolanIbanFormatValidation)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup.string().ibanAo06FormatValidation(angolanIbanFormatValidation),
    }),
    ibanUsd: yup.string().when([], {
      is: () => getFieldObligation('ibanUsd'),
      then: () =>
        yup
          .string()
          .ibanAoFormatValidation(ibanFormatValidation)
          .differentFromField('iban', ibanMustBeDifferent)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .string()
          .ibanAoFormatValidation(ibanFormatValidation)
          .differentFromField('iban', ibanMustBeDifferent),
    }),
    gender: yup.string().when([], {
      is: () => getFieldObligation('gender'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    birthDate: yup.date().when([], {
      is: () => getFieldObligation('birthDate'),
      then: () => yup.date().required(requiredFieldMessage),
      otherwise: () => yup.date(),
    }),
    fatherName: yup.string().when([], {
      is: () => getFieldObligation('fatherName'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    motherName: yup.string().when([], {
      is: () => getFieldObligation('motherName'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    maritalStatus: yup.string().when([], {
      is: () => getFieldObligation('maritalStatus'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    regime: yup.string().when(['maritalStatus'], {
      is: (maritalStatus) =>
        (isStinvestActiveClient
          ? maritalStatus === 'CAS'
          : maritalStatus !== 'ECV_SOLT') && getFieldObligation('regime'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    regimeFree: yup.string().when(['maritalStatus'], {
      is: (maritalStatus) =>
        maritalStatus === 'ECV_CAS' && getFieldObligation('regimeFree'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    address: yup.string().when([], {
      is: () => getFieldObligation('address'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    town: yup.string().when([], {
      is: () => getFieldObligation('town'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    province: yup.string().when([], {
      is: () => getFieldObligation('province'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    country: yup.string().when([], {
      is: () => getFieldObligation('country'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    postCode: yup.string().when([], {
      is: () => getFieldObligation('postCode'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    telephoneNum: yup.number('NUMBER').when([], {
      is: () => getFieldObligation('telephoneNum'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    phoneNumIndicator: yup.number('NUMBER').when([], {
      is: () => getFieldObligation('phoneNumIndicator'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    phoneNum: yup.number('NUMBER').when([], {
      is: () => getFieldObligation('phoneNum'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    phoneNum2Indicator: yup.number('NUMBER').when([], {
      is: () => getFieldObligation('phoneNum2Indicator'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    phoneNum2: yup.number('NUMBER').when([], {
      is: () => getFieldObligation('phoneNum2'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    email1: yup.string().when([], {
      is: () => getFieldObligation('email1'),
      then: () =>
        yup.string().required(requiredFieldMessage).email(fieldMustBeEmail),
      otherwise: () => yup.string().email(fieldMustBeEmail),
    }),
    email2: yup.string().when([], {
      is: () => getFieldObligation('email2'),
      then: () =>
        yup.string().required(requiredFieldMessage).email(fieldMustBeEmail),
      otherwise: () => yup.string().email(fieldMustBeEmail),
    }),
    signatureNum: yup.number('NUMBER').when([], {
      is: () => getFieldObligation('signatureNum'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    employeeType: yup.lazy(() => {
      if (isHemeraActiveClient || isStinvestActiveClient)
        return yup.string().when([], {
          is: () => getFieldObligation('employeeType'),
          then: () => yup.string().required(requiredFieldMessage),
          otherwise: () => yup.string(),
        });

      return yup
        .array()
        .of(yup.string())
        .when([], {
          is: () => getFieldObligation('employeeType'),
          then: () => yup.array().of(yup.string()).min(1, requiredFieldMessage),
          otherwise: () => yup.array().of(yup.string()),
        });
    }),
    employeeTypeDescr: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_OUT') &&
        getFieldObligation('employeeTypeDescr'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    job: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        (employeeType?.includes('TTB_CO') ||
          employeeType?.includes('TTB_CP')) &&
        getFieldObligation('job'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    employer: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_CO') && getFieldObligation('employer'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    admissionDate: yup.date().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_CO') && getFieldObligation('admissionDate'),
      then: () => yup.date().required(requiredFieldMessage),
      otherwise: () => yup.date(),
    }),
    profContractTerm: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_CO') &&
        getFieldObligation('profContractTerm'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    role: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_CO') && getFieldObligation('role'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    cpActivity: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_CP') && getFieldObligation('cpActivity'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    profCommercialName: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_EMP') &&
        getFieldObligation('profCommercialName'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    profNif: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_EMP') && getFieldObligation('profNif'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    indivEntrepActivity: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_EMP') &&
        getFieldObligation('indivEntrepActivity'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    headOffice: yup.string().when(['employeeType'], {
      is: (employeeType) =>
        employeeType?.includes('TTB_EMP') && getFieldObligation('headOffice'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    profCountries: yup.string().when([], {
      is: () => getFieldObligation('profCountries'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    profStatus: yup
      .array()
      .of(yup.string())
      .when([], {
        is: () => getFieldObligation('profStatus'),
        then: () => yup.array().of(yup.string()).min(1, requiredFieldMessage),
        otherwise: () => yup.array().of(yup.string()),
      }),
    profStatusOthers: yup.string().when(['profStatus'], {
      is: (profStatus) =>
        profStatus?.includes('EPF_OUT') &&
        getFieldObligation('profStatusOthers'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppe: yup.string().when([], {
      is: () => getFieldObligation('ppe'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppePosition: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppePosition'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeEntity: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppeEntity'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeInstitution: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppeInstitution'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppePositionHolder: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppePositionHolder'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeOthersName: yup.string().when(['ppe', 'ppePositionHolder'], {
      is: (ppe, ppePositionHolder) =>
        ppe === 'Y' &&
        ppePositionHolder === 'O' &&
        getFieldObligation('ppeOthersName'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppePositionRelation: yup.string().when(['ppe', 'ppePositionHolder'], {
      is: (ppe, ppePositionHolder) =>
        ppe === 'Y' &&
        ppePositionHolder === 'O' &&
        getFieldObligation('ppePositionRelation'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeAppointDate: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppeAppointDate'),
      then: () => yup.date().required(requiredFieldMessage),
      otherwise: () => yup.date(),
    }),
    ppeExonDate: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppeExonDate'),
      then: () => yup.date().required(requiredFieldMessage),
      otherwise: () => yup.date(),
    }),
    ppeFamily: yup.string().when([], {
      is: () => getFieldObligation('ppeFamily'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeKinshipDeg: yup.string().when(['ppeFamily'], {
      is: (ppeFamily) =>
        ppeFamily === 'Y' && getFieldObligation('ppeKinshipDeg'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeRelationship: yup.string().when([], {
      is: () => getFieldObligation('ppeRelationship'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeLeadershipMember: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppeLeadershipMember'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeLeadershipMemberRole: yup.string().when(['ppe', 'ppeLeadershipMember'], {
      is: (ppe, ppeLeadershipMember) =>
        ppe === 'Y' &&
        ppeLeadershipMember === 'Y' &&
        getFieldObligation('ppeLeadershipMemberRole'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeReligiousOrgOfficer: yup.string().when(['ppe'], {
      is: (ppe) => ppe === 'Y' && getFieldObligation('ppeReligiousOrgOfficer'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ppeReligiousOrgOfficerRole: yup
      .string()
      .when(['ppe', 'ppeReligiousOrgOfficer'], {
        is: (ppe, ppeReligiousOrgOfficer) =>
          ppe === 'Y' &&
          ppeReligiousOrgOfficer === 'Y' &&
          getFieldObligation('ppeReligiousOrgOfficerRole'),
        then: () => yup.string().required(requiredFieldMessage),
        otherwise: () => yup.string(),
      }),
    businessPurpose: yup.string().when([], {
      is: () => getFieldObligation('businessPurpose'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    incomeSource: yup.lazy(() => {
      if (isStinvestActiveClient)
        return yup
          .array()
          .of(yup.string())
          .when([], {
            is: () => getFieldObligation('incomeSource'),
            then: () =>
              yup.array().of(yup.string()).min(1, requiredFieldMessage),
            otherwise: () => yup.array().of(yup.string()),
          });

      return yup.string().when([], {
        is: () => getFieldObligation('incomeSource'),
        then: () => yup.string().required(requiredFieldMessage),
        otherwise: () => yup.string(),
      });
    }),
    incomeSourceOthers: yup.lazy(() => {
      if (isStinvestActiveClient)
        return yup.string().when(['incomeSource'], {
          is: (incomeSource) =>
            incomeSource?.includes('OUTRO') &&
            getFieldObligation('incomeSourceOthers'),
          then: () => yup.string().required(requiredFieldMessage),
          otherwise: () => yup.string(),
        });

      return yup.string().when([], {
        is: () => getFieldObligation('incomeSourceOthers'),
        then: () => yup.string().required(requiredFieldMessage),
        otherwise: () => yup.string(),
      });
    }),
    incomeSourceDescr: yup
      .string()
      .when(['incomeSource', 'incomeSourceOthers'], {
        is: (incomeSource, incomeSourceOthers) =>
          (incomeSource === 'ORR_OUTO' || incomeSourceOthers === 'Y') &&
          getFieldObligation('incomeSourceDescr'),
        then: () => yup.string().required(requiredFieldMessage),
        otherwise: () => yup.string(),
      }),
    incomeLevel: yup.string().when([], {
      is: () => getFieldObligation('incomeLevel'),
      then: () =>
        yup
          .string()
          .trim()
          .noSpacesAllowedForTextField(fieldCanNotHaveSpaces)
          .minValue(0, amountGreaterOrEqual(0))
          .integerOnly(fieldIntegerOnly)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .string()
          .trim()
          .noSpacesAllowedForTextField(fieldCanNotHaveSpaces)
          .minValue(0, amountGreaterOrEqual(0))
          .integerOnly(fieldIntegerOnly),
    }),
    incomeCurrency: yup.string().when([], {
      is: () => getFieldObligation('incomeCurrency'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    incomeLevelInFull: yup.string().when([], {
      is: () => getFieldObligation('incomeLevelInFull'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    incomeValues: yup.string().when([], {
      is: () => getFieldObligation('incomeValues'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    relationAssetsOthers: yup.string().when([], {
      is: () => getFieldObligation('relationAssetsOthers'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    financialInstr: yup.string().when([], {
      is: () => getFieldObligation('financialInstr'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    marketOrders: yup.string().when([], {
      is: () => getFieldObligation('marketOrders'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    businessSource: yup.string().when([], {
      is: () => getFieldObligation('businessSource'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    regDepFinantialInstr: yup.string().when([], {
      is: () => getFieldObligation('regDepFinantialInstr'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    mainDwelling: yup.string().when([], {
      is: () => getFieldObligation('mainDwelling'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    car: yup.string().when([], {
      is: () => getFieldObligation('car'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    realEstateTotalValue: yup.string().when(['mainDwelling'], {
      is: (mainDwelling) =>
        mainDwelling === 'HBP_PROP' &&
        getFieldObligation('realEstateTotalValue'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    movableAssetsTotalValue: yup.string().when(['car'], {
      is: (car) =>
        car === 'AUT_PROP' && getFieldObligation('movableAssetsTotalValue'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    americanNationality: yup.string().when([], {
      is: () => getFieldObligation('americanNationality'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    usTaxId: yup.number('NUMBER').when(['americanNationality'], {
      is: (americanNationality) =>
        americanNationality === 'Y' && getFieldObligation('usTaxId'),
      then: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces)
          .required(requiredFieldMessage),
      otherwise: () =>
        yup
          .number('NUMBER')
          .typeError(fieldMustBeNumber)
          .noSpacesAllowed(fieldCanNotHaveSpaces),
    }),
    stocks: yup.string().when([], {
      is: () => getFieldObligation('stocks'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    corporateObligations: yup.string().when([], {
      is: () => getFieldObligation('corporateObligations'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    treasuryBonds: yup.string().when([], {
      is: () => getFieldObligation('treasuryBonds'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    treasuryBills: yup.string().when([], {
      is: () => getFieldObligation('treasuryBills'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    collectiveInvestUnits: yup.string().when([], {
      is: () => getFieldObligation('collectiveInvestUnits'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    depositCertificates: yup.string().when([], {
      is: () => getFieldObligation('depositCertificates'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    derivativeInstruments: yup.string().when([], {
      is: () => getFieldObligation('derivativeInstruments'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    structuredProducts: yup.string().when([], {
      is: () => getFieldObligation('structuredProducts'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    commercialPaper: yup.string().when([], {
      is: () => getFieldObligation('commercialPaper'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ordTransInPerson: yup.string().when([], {
      is: () => getFieldObligation('ordTransInPerson'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ordTransHasEmail: yup.string().when([], {
      is: () => getFieldObligation('ordTransHasEmail'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
    ordTransEmail: yup.string().when(['ordTransHasEmail'], {
      is: (ordTransHasEmail) =>
        ordTransHasEmail === 'Y' && getFieldObligation('ordTransEmail'),
      then: () =>
        yup.string().required(requiredFieldMessage).email(fieldMustBeEmail),
      otherwise: () => yup.string().email(fieldMustBeEmail),
    }),
    ordTransEmail2: yup.string().when(['ordTransHasEmail'], {
      is: (ordTransHasEmail) =>
        ordTransHasEmail === 'Y' && getFieldObligation('ordTransEmail2'),
      then: () =>
        yup.string().required(requiredFieldMessage).email(fieldMustBeEmail),
      otherwise: () => yup.string().email(fieldMustBeEmail),
    }),
    ordTransPhoneNum: yup.string().when([], {
      is: () => getFieldObligation('ordTransPhoneNum'),
      then: () => yup.string().required(requiredFieldMessage),
      otherwise: () => yup.string(),
    }),
  });
};
